import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase-config';
import './press.scss';
import { applySeo, siteUrl } from '../../utils/seo';

const parseDateOnly = (value) => {
    if (!value) {
        return null;
    }

    const [year, month, day] = value.split('-').map(Number);

    if (!year || !month || !day) {
        return null;
    }

    return new Date(year, month - 1, day);
};

const Press = () => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        applySeo({
            title: 'Press | David Joyce Astrophotography',
            description: 'Browse articles, interviews, publications, and external features highlighting David Joyce and AstroDavido astrophotography.',
            canonicalPath: '/press/',
            ogImage: `${siteUrl}/logo192.png`,
            structuredData: {
                '@context': 'https://schema.org',
                '@type': 'CollectionPage',
                name: 'Press',
                description: 'A collection of articles, interviews, mentions, and external features related to AstroDavido astrophotography.',
                url: `${siteUrl}/press/`
            }
        });

        const loadFeatures = async () => {
            setLoading(true);
            setError('');

            try {
                const snapshot = await getDocs(collection(db, 'features'));
                const featureList = snapshot.docs
                    .map((feature) => ({ ...feature.data(), firestoreId: feature.id }))
                    .filter((feature) => feature.is_active === 1)
                    .sort((left, right) => {
                        const leftOrder = Number(left.sort_order ?? Number.MAX_SAFE_INTEGER);
                        const rightOrder = Number(right.sort_order ?? Number.MAX_SAFE_INTEGER);

                        if (leftOrder !== rightOrder) {
                            return leftOrder - rightOrder;
                        }

                        const leftDate = left.published_date
                            ? parseDateOnly(left.published_date)?.getTime?.() || 0
                            : left.date_created?.toDate?.()?.getTime?.() || 0;
                        const rightDate = right.published_date
                            ? parseDateOnly(right.published_date)?.getTime?.() || 0
                            : right.date_created?.toDate?.()?.getTime?.() || 0;
                        return rightDate - leftDate;
                    });

                setFeatures(featureList);
            } catch (loadError) {
                setError('Unable to load press mentions right now.');
            } finally {
                setLoading(false);
            }
        };

        loadFeatures();
    }, []);

    const formatFeatureDate = (feature) => {
        if (feature.published_date) {
            return parseDateOnly(feature.published_date)?.toDateString?.() || '';
        }

        if (feature.date_created?.toDate?.()) {
            return feature.date_created.toDate().toDateString();
        }

        return '';
    };

    return (
        <div className='press-page'>
            <div className='press-hero'>
                <h1>Press</h1>
                <p>
                    A collection of articles, interviews, mentions, and other coverage related to my astrophotography.
                </p>
            </div>

            {loading ? <p className='admin-status-message'>Loading press mentions...</p> : null}
            {error ? <p className='admin-form-message error'>{error}</p> : null}

            {!loading && !error ? (
                features.length ? (
                    <div className='press-list'>
                        {features.map((feature) => (
                            <article className='press-card' key={feature.firestoreId}>
                                <div className='press-card-header'>
                                    <div>
                                        <p className='press-publication'>{feature.publication || 'External Feature'}</p>
                                        {formatFeatureDate(feature) ? (
                                            <p className='press-date'>{formatFeatureDate(feature)}</p>
                                        ) : null}
                                    </div>
                                    {feature.feature_type ? <span className='press-type-badge'>{feature.feature_type}</span> : null}
                                </div>

                                <h2>
                                    <a href={feature.url} target='_blank' rel='noreferrer'>
                                        {feature.title}
                                    </a>
                                </h2>

                                {feature.description ? <p className='press-description'>{feature.description}</p> : null}

                                <a className='press-link' href={feature.url} target='_blank' rel='noreferrer'>
                                    Visit Link
                                </a>
                            </article>
                        ))}
                    </div>
                ) : (
                    <p className='admin-status-message'>No press mentions have been added yet.</p>
                )
            ) : null}
        </div>
    );
};

export default Press;
